/**
 * Global Error Fallback Component
 *
 * Used as the error component for TanStack Router routes.
 * Provides a consistent error UI across all routes.
 */

import { Link, useRouter } from "@tanstack/react-router";
import * as Sentry from "@sentry/react";
import { AlertCircle, RefreshCw, Home, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { detectErrorType } from "./ErrorFallback";

interface GlobalErrorFallbackProps {
  error: Error;
  reset?: () => void;
}

/**
 * Get error content based on error type
 */
function getErrorContent(error: Error): { title: string; message: string } {
  const errorType = detectErrorType(error);

  switch (errorType) {
    case "network":
      return {
        title: "Connection Problem",
        message:
          "Unable to connect to our servers. Please check your internet connection and try again.",
      };
    case "not_found":
      return {
        title: "Page Not Found",
        message:
          "The page you're looking for doesn't exist or may have been moved.",
      };
    case "server":
      return {
        title: "Server Error",
        message:
          "Something went wrong on our end. Our team has been notified and is working on a fix.",
      };
    default:
      return {
        title: "Something Went Wrong",
        message:
          "An unexpected error occurred. Please try again or contact support if the problem persists.",
      };
  }
}

/**
 * Global error fallback for TanStack Router
 *
 * @example
 * // In route definition
 * export const Route = createFileRoute("/some-route")({
 *   component: SomeComponent,
 *   errorComponent: GlobalErrorFallback,
 * });
 */
export function GlobalErrorFallback({ error, reset }: GlobalErrorFallbackProps) {
  const router = useRouter();
  const { title, message } = getErrorContent(error);

  // Report to Sentry
  Sentry.withScope((scope) => {
    scope.setTag("error_source", "route_error_boundary");
    scope.setTag("error_type", detectErrorType(error));
    scope.setExtra("current_path", router.state.location.pathname);
    Sentry.captureException(error);
  });

  const handleRetry = () => {
    if (reset) {
      reset();
    } else {
      router.invalidate();
    }
  };

  const handleGoBack = () => {
    router.history.back();
  };

  return (
    <div className="container mx-auto max-w-xl px-4 py-12">
      <div className="text-center">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
          <AlertCircle className="h-8 w-8 text-destructive" />
        </div>

        <h1 className="mt-6 text-2xl font-bold text-foreground">{title}</h1>
        <p className="mt-3 text-muted-foreground">{message}</p>

        {import.meta.env.DEV && (
          <details className="mt-6 text-left">
            <summary className="cursor-pointer text-sm text-muted-foreground hover:text-foreground">
              Technical Details
            </summary>
            <pre className="mt-2 overflow-auto rounded-lg bg-muted p-4 text-xs text-muted-foreground max-h-48">
              {error.stack ?? error.message}
            </pre>
          </details>
        )}

        <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-center">
          <Button onClick={handleRetry} className="gap-2">
            <RefreshCw className="h-4 w-4" />
            Try Again
          </Button>
          <Button variant="outline" onClick={handleGoBack} className="gap-2">
            <ArrowLeft className="h-4 w-4" />
            Go Back
          </Button>
          <Button variant="ghost" asChild className="gap-2">
            <Link to="/">
              <Home className="h-4 w-4" />
              Home
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Not Found Error Component
 * Used for 404 pages
 */
export function NotFoundError() {
  const router = useRouter();

  const handleGoBack = () => {
    router.history.back();
  };

  return (
    <div className="container mx-auto max-w-xl px-4 py-12">
      <div className="text-center">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-muted">
          <span className="text-3xl font-bold text-muted-foreground">404</span>
        </div>

        <h1 className="mt-6 text-2xl font-bold text-foreground">Page Not Found</h1>
        <p className="mt-3 text-muted-foreground">
          The page you're looking for doesn't exist or may have been moved.
        </p>

        <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-center">
          <Button variant="outline" onClick={handleGoBack} className="gap-2">
            <ArrowLeft className="h-4 w-4" />
            Go Back
          </Button>
          <Button asChild className="gap-2">
            <Link to="/">
              <Home className="h-4 w-4" />
              Go Home
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
